'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { triggerPriceTypeEnum, type TriggerPriceType } from '@flipster/apis/v2/trade/types';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';

const DropdownSelect = createDropdownSelect<TriggerPriceType>();

const triggerPriceTypes = [triggerPriceTypeEnum.last, triggerPriceTypeEnum.mark] as const;

export type TriggerPriceTypeSelectPlacement = 'label-suffix' | 'in-input';

export { triggerPriceTypeEnum, type TriggerPriceType };

export const defaultTriggerPriceType = triggerPriceTypeEnum.last;

const baseTriggerCss = {
  gap: 4,
  alignItems: 'center',
  color: 'content.default.level-4',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
} as const;

const triggerCssByPlacement: Record<TriggerPriceTypeSelectPlacement, ReturnType<typeof hstack.raw>> = {
  'label-suffix': hstack.raw({ ...baseTriggerCss, textStyle: 'Text/M/Strong', color: 'content.default.level-2' }),
  // 입력 프레임 내부(suffix)에 들어가므로 자체 테두리/높이 없이 텍스트만 노출한다.
  'in-input': hstack.raw({ ...baseTriggerCss, textStyle: 'Text/M/Strong' }),
};

export interface TriggerPriceTypeSelectProps {
  value: TriggerPriceType;
  onChange: (value: TriggerPriceType) => void;
  placement: TriggerPriceTypeSelectPlacement;
  disabled?: boolean;
}

export const TriggerPriceTypeSelect = ({ value, onChange, placement, disabled }: TriggerPriceTypeSelectProps) => {
  const t = useTranslations();
  const labels = useMemo(
    (): Record<TriggerPriceType, string> => ({
      [triggerPriceTypeEnum.last]: t('Common.r260623.Last_price_short'),
      [triggerPriceTypeEnum.mark]: t('Autogen_common.r251126.s_Mark'),
    }),
    [t],
  );
  const triggerCss = triggerCssByPlacement[placement];

  return (
    <DropdownSelect.Root selected={value} onChange={onChange}>
      <DropdownSelect.Trigger disabled={disabled} css={triggerCss}>
        {labels[value]}
        <ChevronDownIcon
          size={placement === 'label-suffix' ? 14 : 16}
          className={css({
            transition: 'transform 0.2s',
            '[aria-expanded=true] &': { transform: 'rotate(-180deg)' },
          })}
        />
      </DropdownSelect.Trigger>
      <DropdownSelect.Select portal css={{ minW: 140 }}>
        {triggerPriceTypes.map((type) => (
          <DropdownSelect.Item key={type} value={type} label={labels[type]} />
        ))}
      </DropdownSelect.Select>
    </DropdownSelect.Root>
  );
};
